// Seção "Cadastro" da ficha do profissional no hub RH.
//
// ORIGEM: extraída de pages/Profissionais.tsx (o miolo do card de edição,
// linhas ~509-787, em 2026-08-25) — aquela tela será aposentada quando a
// ficha entrar no ar. Diferença deliberada da tela antiga: aqui NÃO há modo
// resumo/edição alternado por lista — a seção abre direto em modo edição dos
// próprios campos, com botão Salvar.
//
// As QUATRO vias de escrita moram DENTRO deste componente:
//   1. PUT /profissionais/{id}          — rateio, categorias rateáveis,
//      salário/coeficiente, horas/dia, e-mail de login (a marca "Entra no
//      rateio por horas" saiu em 06/10/2026: toda remuneração com a pessoa
//      segue as horas anotadas)
//      (dre-engine; remuneração com gate financeiro.profissionais.ver no
//      servidor — quem não tem a perm nem recebe os campos)
//   2. action profissional_clockify_set — clockify_user_id (comercial-engine;
//      morre junto com o Clockify, por isso não migrou para o PUT)
//   3. action profissional_clockify_set — ativo (arquivar/reativar)
//   4. POST/DELETE /profissionais/{id}/alias
// O pai só recebe `aoSalvar` para recarregar os dados depois de qualquer
// escrita bem-sucedida.
import { ReactElement, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, Button, Checkbox, Divider, Flex, InputNumber, Select,
  Space, Tag, Tooltip, Typography, App as AntApp,
} from 'antd';
import {
  CloseOutlined, LinkOutlined, PlusOutlined, SaveOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import HistoricoRemuneracao from '../profissionais/HistoricoRemuneracao';
import { usePerms } from '../../hooks/usePerms';
import ModalDesligarPessoa, { resumoDesligamento } from '../../components/pessoas/ModalDesligarPessoa';
import ModalReativarPessoa from '../../components/pessoas/ModalReativarPessoa';
import ModalVincularLogin from '../../components/pessoas/ModalVincularLogin';
import ModalNovaPessoa from '../../components/pessoas/ModalNovaPessoa';

const { Text } = Typography;

export const CATEGORIAS_RATEIO = [
  { label: 'Remuneração de Sócios', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Remuneração de Autônomos', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Remuneração CLT', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Remuneração de Estagiários', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Remuneração de Auxiliares e Freelancers', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Honorários Terceirizados', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Remuneração de Autônomos - Comercial', grupo: 'DESPESAS_COMERCIAIS' },
  { label: 'Mão de obra administrativa', grupo: 'DESPESAS_ADMINISTRATIVAS' },
];

export interface Regra {
  categoria_dre: string;
  grupo_dre: string;
  percentual: number;
}

export interface Profissional {
  id: string;
  nome_canonical: string;
  ativo: boolean;
  regras_rateio: Regra[];
  aliases: string[];
  categorias_rateaveis: string[] | null;
  categorias_historicas: string[];
  // Vêm do GET /profissionais desde 2026-08-21. `salario_esperado` e
  // `coeficiente` só chegam para quem tem `financeiro.profissionais.ver`;
  // para os demais a chave nem existe na resposta (ficha podada no servidor,
  // não um 403 — 403 confirmaria que o campo existe).
  horas_dia?: string | null;
  usuario_email?: string | null;
  salario_esperado?: string | null;
  coeficiente?: string | null;
}

/** O que vem do mapa de config do Clockify (action do comercial-engine). */
export interface ClockifyCfg {
  salario_esperado: number | null;
  coeficiente: number | null;
  usa_clockify_rateio: boolean;
  clockify_user_id: string | null;
  clockify_user_email: string | null;
  clockify_user_name: string | null;
}

/** O que o formulário edita. Além da config do Clockify, os dois campos que
 *  vivem em `public.profissionais` e vêm do GET /profissionais — separados
 *  porque o `ClockifyCfg` morre junto com o Clockify e estes ficam. */
export interface CadastroEdit extends ClockifyCfg {
  horas_dia: number | null;
  usuario_email: string | null;
}

const CFG_VAZIA: ClockifyCfg = {
  salario_esperado: null, coeficiente: 1.0, usa_clockify_rateio: false,
  clockify_user_id: null, clockify_user_email: null, clockify_user_name: null,
};

// numeric do Postgres chega como string; parseFloat antes de usar.
function num(v: any, padrao: number | null): number | null {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : padrao;
}

function estadoInicial(p: Profissional, cfg: ClockifyCfg | undefined): CadastroEdit {
  return {
    ...(cfg || CFG_VAZIA),
    // `horas_dia` e `usuario_email` vêm do GET /profissionais (dre-engine), e
    // não do mapa de config do Clockify — que morre junto com o Clockify.
    horas_dia: num(p.horas_dia, 8),
    usuario_email: p.usuario_email ?? null,
    // 🚨 Salário e coeficiente saem SÓ do GET /profissionais, que os PODA para
    //    quem não tem `financeiro.profissionais.ver` (a chave some do objeto).
    //    Semeá-los do mapa do Clockify punha o valor real na tela de quem não
    //    pode vê-lo — e ainda com o aviso "você não tem acesso" logo abaixo
    //    (achado da revisão de 25/08). A porta antiga também foi gateada, mas
    //    a tela não pode DEPENDER disso.
    //    (numeric do Postgres chega como STRING — Number() antes de guardar)
    salario_esperado: 'salario_esperado' in p && p.salario_esperado !== null
      && p.salario_esperado !== undefined ? Number(p.salario_esperado) : null,
    coeficiente: 'salario_esperado' in p && p.coeficiente !== null
      && p.coeficiente !== undefined ? Number(p.coeficiente) : null,
  };
}

export default function SecaoCadastro({
  profissional, clockifyCfg, clockifyUsers, usuariosSistema,
  todosProfissionais, aoSalvar,
}: {
  profissional: Profissional;
  /** Config Clockify da pessoa (action profissional_clockify_listar); pode não existir. */
  clockifyCfg?: ClockifyCfg;
  clockifyUsers: any[];
  /** Pessoas com login no sistema, para sugerir no campo de vínculo. */
  usuariosSistema: { email: string; nome: string }[];
  /** Lista completa — alimenta o aviso "já em uso" do e-mail e o select de aliases. */
  todosProfissionais: Profissional[];
  /** Chamado depois de qualquer escrita ok — o pai recarrega os dados. */
  aoSalvar: () => void;
}): ReactElement {
  const { message } = AntApp.useApp();
  // Sinal ÚNICO da permissão de remuneração nesta tela: o servidor PODA as
  // chaves salario_esperado/coeficiente do GET /profissionais para quem não
  // tem `financeiro.profissionais.ver` (deleta as chaves, não devolve 403).
  const podeRemun = 'salario_esperado' in profissional;
  const [regrasEdit, setRegrasEdit] = useState<Regra[]>(
    () => [...(profissional.regras_rateio || [])]);
  const [categoriasEdit, setCategoriasEdit] = useState<string[]>(
    () => Array.isArray(profissional.categorias_rateaveis)
      ? [...profissional.categorias_rateaveis]
      : [...(profissional.categorias_historicas || [])]);
  const [clockifyEdit, setClockifyEdit] = useState<CadastroEdit>(
    () => estadoInicial(profissional, clockifyCfg));
  const [novoAlias, setNovoAlias] = useState<string | undefined>(undefined);
  const [salvando, setSalvando] = useState(false);

  // Re-sincroniza quando o pai recarrega (aoSalvar → fetch → props novas) ou
  // quando a ficha troca de pessoa. Equivale ao iniciarEdicao() da tela antiga.
  useEffect(() => {
    // SEM pré-preenchimento de rateio (Lucas 2026-07-08): o default "100% →
    // Remuneração de Autônomos" foi salvo em massa e reenquadrava sócios,
    // estagiários e CLT. Sem regra = 100% na categoria original.
    setRegrasEdit([...(profissional.regras_rateio || [])]);
    // Respeita array vazio (intencional do operador) vs null (nunca
    // configurado). Bug anterior: tratava [] como "nunca configurado".
    setCategoriasEdit(
      Array.isArray(profissional.categorias_rateaveis)
        ? [...profissional.categorias_rateaveis]
        : [...(profissional.categorias_historicas || [])]);
    setClockifyEdit(estadoInicial(profissional, clockifyCfg));
    setNovoAlias(undefined);
  }, [profissional, clockifyCfg]);


  function toggleCategoria(cat: string) {
    setCategoriasEdit(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  }

  function adicionarRegra() {
    setRegrasEdit([...regrasEdit, {
      categoria_dre: CATEGORIAS_RATEIO[0].label,
      grupo_dre: CATEGORIAS_RATEIO[0].grupo,
      percentual: 0,
    }]);
  }

  function removerRegra(i: number) {
    setRegrasEdit(regrasEdit.filter((_, idx) => idx !== i));
  }

  function atualizarRegra(i: number, campo: 'categoria_dre' | 'percentual', valor: any) {
    const novas = [...regrasEdit];
    if (campo === 'categoria_dre') {
      const cat = CATEGORIAS_RATEIO.find(c => c.label === valor);
      novas[i] = { ...novas[i], categoria_dre: valor, grupo_dre: cat?.grupo || '' };
    } else {
      novas[i] = { ...novas[i], [campo]: valor };
    }
    setRegrasEdit(novas);
  }

  const totalPct = regrasEdit.reduce((s, r) => s + (Number(r.percentual) || 0), 0);

  async function salvar() {
    // Lista vazia é válida: sem rateio = 100% na categoria original.
    if (regrasEdit.length > 0 && totalPct !== 100) {
      message.warning('Os percentuais devem somar 100% (ou remova todas as regras pra manter na categoria original).');
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      // UMA chamada só, para o dre-engine (2026-08-21). Antes eram duas: o
      // rateio aqui e a remuneração numa action do comercial-engine que NÃO
      // tinha gate nenhum — qualquer pessoa logada editava salário. Além do
      // gate, esta Lambda o CI publica; a outra é deploy manual, e gate que
      // espera publicação manual fica meses sem subir.
      await axios.put(`${API_URL}/profissionais/${profissional.id}`,
        {
          regras_rateio: regrasEdit,
          categorias_rateaveis: categoriasEdit,
          // Sem a perm de remuneração as chaves nem VÃO: o dre-engine exige
          // `financeiro.profissionais.editar` para elas e recusaria o PUT
          // inteiro — o rateio ficaria impossível de salvar por tabela.
          ...(podeRemun ? {
            salario_esperado: clockifyEdit.salario_esperado,
            coeficiente: clockifyEdit.coeficiente,
          } : {}),
          horas_dia: clockifyEdit.horas_dia,
          // `usuario_email` não vai mais: o vínculo login ↔ ficha é do ciclo da
          // pessoa (botão "Vincular login", só administrador) e religa as horas.
        },
        { headers: { Authorization: token } }
      );
      // O vínculo com o usuário do Clockify continua na action antiga: ela
      // morre junto com o Clockify, e trazer para cá agora seria mudar código
      // que já tem data para sumir.
      if (clockifyEdit.clockify_user_id !== undefined) {
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'profissional_clockify_set', id: profissional.id,
            clockify_user_id: clockifyEdit.clockify_user_id || null },
          { headers: { Authorization: token } }
        );
      }
      message.success('Cadastro salvo.');
      aoSalvar();
    } catch (e: any) {
      message.error('Erro ao salvar: ' + (e.response?.data?.error || e.message));
    } finally {
      setSalvando(false);
    }
  }

  async function adicionarAlias() {
    if (!novoAlias) return;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/profissionais/${profissional.id}/alias`,
        { alias: novoAlias },
        { headers: { Authorization: token } }
      );
      setNovoAlias(undefined);
      aoSalvar();
    } catch {
      message.error('Erro ao adicionar alias.');
    }
  }

  async function removerAlias(alias: string) {
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/profissionais/${profissional.id}/alias/${encodeURIComponent(alias)}`,
        { headers: { Authorization: token } }
      );
      aoSalvar();
    } catch {
      message.error('Erro ao remover alias.');
    }
  }

  // Arquivar/reativar e vincular login viraram ações do ciclo da pessoa
  // (28/09/2026): Desligar pede a data de saída e cuida de login, perfis,
  // férias e cobrança; só o administrador do sistema faz.
  const { pode } = usePerms();
  const podeCiclo = pode('admin.permissoes.editar');
  const [desligando, setDesligando] = useState(false);
  const [reativando, setReativando] = useState(false);
  const [vinculando, setVinculando] = useState(false);
  const [criandoLogin, setCriandoLogin] = useState(false);

  const aliasesVisiveis = (profissional.aliases || [])
    .filter(a => a !== profissional.nome_canonical);

  return (
    <div>
      {!profissional.ativo && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Ex-funcionário"
          description="Fora das listas, do auto-preencher e do rateio Clockify. Os lançamentos antigos ficam intocados. Dá para reativar no botão lá embaixo." />
      )}

      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Regras de rateio
      </Text>
      <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 8 }}>
        {regrasEdit.map((r, i) => (
          <Flex key={i} gap={8} align="center">
            <Select
              value={r.categoria_dre}
              onChange={(v) => atualizarRegra(i, 'categoria_dre', v)}
              options={CATEGORIAS_RATEIO.map(c => ({ label: c.label, value: c.label }))}
              style={{ flex: 1 }}
              showSearch
            />
            <InputNumber
              value={r.percentual}
              onChange={(v) => atualizarRegra(i, 'percentual', Number(v) || 0)}
              min={0}
              max={100}
              suffix="%"
              style={{ width: 90 }}
            />
            <Button
              danger
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => removerRegra(i)}
            />
          </Flex>
        ))}
      </Space>

      <Flex justify="space-between" align="center" style={{ marginTop: 8 }}>
        <Button size="small" icon={<PlusOutlined />} onClick={adicionarRegra}>
          Adicionar linha
        </Button>
        <Text strong style={{ color: regrasEdit.length === 0 ? '#888' : (totalPct === 100 ? '#0930D0' : '#B91626'), fontSize: 12 }}>
          {regrasEdit.length === 0
            ? 'Sem rateio — fica na categoria original'
            : `Total: ${totalPct}%`}
        </Text>
      </Flex>

      <Divider style={{ margin: '14px 0' }} />

      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Categorias rateáveis
      </Text>
      <div style={{ fontSize: 11, color: '#888', marginTop: 4, marginBottom: 8 }}>
        Marque as categorias do ContaAzul em que os lançamentos deste profissional devem ser rateados.
        As desmarcadas ficam na linha original da DRE sem rateio.
      </div>
      {profissional.categorias_historicas && profissional.categorias_historicas.length > 0 ? (
        <Space size={[6, 6]} wrap>
          {profissional.categorias_historicas.map(cat => (
            <Checkbox
              key={cat}
              checked={categoriasEdit.includes(cat)}
              onChange={() => toggleCategoria(cat)}
            >
              <Text style={{ fontSize: 12 }}>{cat}</Text>
            </Checkbox>
          ))}
        </Space>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Sem lançamentos históricos. Categorias aparecerão aqui após importação.
        </Text>
      )}

      <Divider style={{ margin: '14px 0' }} />

      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        ⏱️ Apontamento de horas
      </Text>
      <Flex gap={12} wrap style={{ marginTop: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Horas por dia</div>
          <InputNumber
            value={clockifyEdit.horas_dia}
            onChange={(v) => setClockifyEdit({ ...clockifyEdit, horas_dia: v === null || v === undefined ? null : Number(v) })}
            min={1} max={24} step={1} precision={2}
            style={{ width: 120 }} placeholder="8"
          />
          <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
            Meio período? 4 ou 6. Entra no R$/h do fechamento.
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 300 }}>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Pessoa no sistema (login)</div>
          {/* Desde 28/09/2026 o vínculo é do ciclo da pessoa: escolher um login
              solto (ou criar um) religa as horas já anotadas. Digitar e-mail à
              mão era como se errava a letra e a hora não virava custo. */}
          <Flex gap={8} align="center" wrap>
            <Text style={{ fontSize: 13 }}>
              {clockifyEdit.usuario_email
                ? <span><strong>{clockifyEdit.usuario_email}</strong></span>
                : <Tag color="orange">sem login — as horas desta pessoa não viram custo</Tag>}
            </Text>
            {podeCiclo && !clockifyEdit.usuario_email && (
              <Button size="small" type="primary" onClick={() => setVinculando(true)}>Vincular login</Button>
            )}
          </Flex>
        </div>
      </Flex>

      <Divider style={{ margin: '14px 0' }} />

      {/* Bloco renomeado em 2026-08-21: chamava-se "Rateio mensal por
          Clockify", e por isso ninguém achava a remuneração — salário e
          coeficiente moravam debaixo de um título que falava de outra coisa. */}
      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        💰 Remuneração
      </Text>
      <div style={{ fontSize: 11, color: '#888', marginTop: 4, marginBottom: 8 }}>
        O custo que entra no lucro por contrato NÃO sai daqui: sai da nota de
        pagamento de cada mês, então aumento entra sozinho. Estes campos servem para
        conferir (alerta se o lançamento do mês divergir mais de 5%) e para projetar
        encargos pelo coeficiente.
      </div>
      <Flex gap={12} wrap>
        {podeRemun && <div>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Salário esperado (R$)</div>
          <InputNumber
            value={clockifyEdit.salario_esperado}
            onChange={(v) => setClockifyEdit({ ...clockifyEdit, salario_esperado: v === null || v === undefined ? null : Number(v) })}
            min={0}
            precision={2}
            style={{ width: 180 }}
            placeholder="ex: 10000,00"
          />
        </div>}
        {podeRemun && <div>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Coeficiente</div>
          <InputNumber
            value={clockifyEdit.coeficiente}
            onChange={(v) => setClockifyEdit({ ...clockifyEdit, coeficiente: v === null || v === undefined ? null : Number(v) })}
            min={0}
            max={5}
            step={0.05}
            precision={4}
            style={{ width: 110 }}
            placeholder="1,0"
          />
        </div>}
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>
            Usuário Clockify
            <Tag style={{ marginLeft: 6, fontSize: 10 }}>sai no corte</Tag>
          </div>
          <Select
            value={clockifyEdit.clockify_user_id || undefined}
            onChange={(v) => {
              const u = clockifyUsers.find(uu => uu.clockify_user_id === v);
              setClockifyEdit({ ...clockifyEdit,
                clockify_user_id: v || null,
                clockify_user_email: u?.email || null,
                clockify_user_name: u?.clockify_user_name || null });
            }}
            placeholder="— sem vínculo —"
            allowClear showSearch optionFilterProp="label"
            style={{ width: '100%' }}
            options={clockifyUsers.map((u: any) => ({
              label: `${u.clockify_user_name || u.email} (${u.email})`,
              value: u.clockify_user_id,
            }))}
          />
        </div>
      </Flex>
      {podeRemun && clockifyEdit.salario_esperado !== null && clockifyEdit.coeficiente !== null && (
        <div style={{ fontSize: 11, color: '#0930D0', marginTop: 6 }}>
          Valor esperado mensal: <strong>R$ {(clockifyEdit.salario_esperado * clockifyEdit.coeficiente).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
          {' '}— alerta se o lançamento divergir &gt; 5%
        </div>
      )}
      {/* Quem não tem `financeiro.profissionais.ver` não recebe os campos do
          servidor. Dizer isso é melhor do que deixar a pessoa achar que o
          cadastro está vazio. */}
      {!podeRemun && (
        <Alert type="info" showIcon style={{ marginTop: 8 }}
          message="Você não tem acesso à remuneração"
          description="Os campos de salário e coeficiente não são carregados para o seu perfil. O resto do cadastro continua editável." />
      )}

      <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
        Sem divisão fixa acima, a remuneração lançada com esta pessoa no Contas a Pagar é dividida pelas horas
        que ela anotar no mês (Operacional › Horas, ou Clockify): hora em contrato vai para o contrato, hora em
        centro interno é despesa, e o que não for anotado fica parado até ser corrigido.
      </Text>

      {'salario_esperado' in profissional && (
        <>
          <Divider style={{ margin: '14px 0' }} />
          <HistoricoRemuneracao profissionalId={profissional.id} aoMudar={aoSalvar} />
        </>
      )}

      <Divider style={{ margin: '14px 0' }} />

      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Nomes alternativos (aliases)
      </Text>
      {aliasesVisiveis.length > 0 && (
        <Space size={4} wrap style={{ marginTop: 6, display: 'flex' }}>
          {aliasesVisiveis.map(a => (
            <Tag
              key={a}
              closable
              onClose={(e) => { e.preventDefault(); removerAlias(a); }}
              style={{ margin: 0, fontSize: 11 }}
            >
              {a}
            </Tag>
          ))}
        </Space>
      )}
      <Flex gap={8} style={{ marginTop: 8 }}>
        <Select
          placeholder="Selecionar nome para vincular..."
          value={novoAlias}
          onChange={setNovoAlias}
          options={todosProfissionais
            .filter(outro => outro.id !== profissional.id && !profissional.aliases?.includes(outro.nome_canonical))
            .map(outro => ({ label: outro.nome_canonical, value: outro.nome_canonical }))
          }
          showSearch
          style={{ flex: 1 }}
        />
        <Button
          disabled={!novoAlias}
          onClick={adicionarAlias}
          icon={<LinkOutlined />}
        >
          Vincular
        </Button>
      </Flex>
      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
        Ao vincular, os lançamentos do nome selecionado passarão a usar as regras deste profissional.
      </div>

      <Tooltip title={regrasEdit.length > 0 && totalPct !== 100
        ? `As linhas de rateio somam ${totalPct}% — ajuste para 100% ou remova todas.` : ''}>
        <Button
          type="primary"
          block
          icon={<SaveOutlined />}
          style={{ marginTop: 16 }}
          disabled={regrasEdit.length > 0 && totalPct !== 100}
          loading={salvando}
          onClick={salvar}
        >
          Salvar
        </Button>
      </Tooltip>

      <Flex justify="center" style={{ marginTop: 8 }}>
        {!podeCiclo ? (
          <Text type="secondary" style={{ fontSize: 11 }}>
            Desligar ou reativar esta pessoa é com o administrador do sistema (pede a data de saída).
          </Text>
        ) : profissional.ativo ? (
          <Button size="small" type="text" danger onClick={() => setDesligando(true)}>
            Desligar (informa a data de saída)
          </Button>
        ) : (
          <Button size="small" type="text" onClick={() => setReativando(true)}>
            Reativar
          </Button>
        )}
      </Flex>

      {desligando && (
        <ModalDesligarPessoa aberto pessoa={{ id: profissional.id, nome: profissional.nome_canonical, usuario_email: clockifyEdit.usuario_email }}
          onClose={() => setDesligando(false)}
          onSalvo={(r) => { setDesligando(false); message.success(resumoDesligamento(r), 10); aoSalvar(); }} />
      )}
      {reativando && (
        <ModalReativarPessoa aberto pessoa={{ id: profissional.id, nome: profissional.nome_canonical, usuario_email: clockifyEdit.usuario_email }}
          onClose={() => setReativando(false)}
          onSalvo={() => { setReativando(false); message.success('Reativado.'); aoSalvar(); }} />
      )}
      {vinculando && (
        <ModalVincularLogin aberto ficha={{ id: profissional.id, nome: profissional.nome_canonical }}
          onClose={() => setVinculando(false)}
          onSalvo={(r) => { setVinculando(false); message.success(`Login vinculado.${r.religados ? ` ${r.religados} hora(s) antiga(s) passaram a contar para esta ficha.` : ''}`, 8); aoSalvar(); }}
          onCriarLogin={() => setCriandoLogin(true)} />
      )}
      {criandoLogin && (
        <ModalNovaPessoa aberto religar={{ id: profissional.id, nome: profissional.nome_canonical }}
          onClose={() => setCriandoLogin(false)} onSalvo={() => aoSalvar()} />
      )}
    </div>
  );
}
